import { thresholds } from '../common/constants';
import {
    Column,
    Filters,
    FilterValue,
    IdType,
    TableInstance
} from 'react-table';
import { Comparator } from 'hooks/useQueryFilter';
import { ReactSelectOption, Transmitter } from 'types';
import { Protocol  } from 'types/enum';
import { ColumnInfo } from 'components/monitor/ColumnsVisibilityMenuCheckboxes';
import { calculateCRC8 } from 'lib/crc';

export const isServer = typeof window === 'undefined';

export const isDev = () =>
    !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

export const isIE = () => {
    if (!isServer) {
        const ua = window.navigator.userAgent;
        const msie = ua.indexOf('MSIE ') > -1;
        const msie11 = ua.indexOf('Trident/') > -1;

        // If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
        // const isEdge = ua.indexOf("Edge/") > -1;

        return msie || msie11;
    }

    return false;
};

export const isValidSensorTypeId = (sensorTypeId: number) => {
    return sensorTypeId <= thresholds.SENSOR_TYPE_VALID;
};

export const uniqueBy = <T extends Record<string, any>>(
    arr: T[],
    key: string
) => {
    const result: T[] = [];
    const map = new Map();
    arr.forEach(item => {
        if (!map.has(item[key])) {
            map.set(item[key], true);
            result.push(item);
        }
    });

    return result;
};

export const isArray = <T>(value: unknown): value is T[] =>
    Array.isArray(value);

export const isObjectType = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]';

export const isObject = <T extends object>(value: unknown): value is T =>
    Object.prototype.toString.call(value) === '[object Object]';

export const insertAt = <T>(
    data: T[],
    index: number,
    value?: T | T[]
): (T | null)[] => {
    return [
        ...data.slice(0, index),
        ...(isArray(value) ? value : [value || null]),
        ...data.slice(index)
    ];
};

export const updateAt = <T>(
    data: T[],
    index: number,
    value: Partial<T> | T | T[]
): T[] => {
    if (index < 0) return data;

    return [
        ...data.slice(0, index),
        isObject(value) ? { ...data[index], ...value } : value,
        ...data.slice(index + 1)
    ];
};

export const append = <T>(data: T[], value: T | T[]): T[] => [
    ...data,
    ...(isArray(value) ? value : [value])
];

export const removeAt = <T>(data: T[], index: number): T[] => [
    ...data.slice(0, index),
    ...data.slice(index + 1)
];

export const isEmptyObject = (value: unknown): boolean => {
    if (Object.prototype.toString.call(value) !== '[object Object]') return false;
    for (const key in value as object) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
            return false;
        }
    }
    return true;
};

export type SafeStringArg = string | number | null | undefined;

// return null if any of the arguments is null or undefined
export const safestring = (
    strings: TemplateStringsArray,
    ...args: SafeStringArg[]
): string | null => {
    let result = strings[0];
    for (let index = 0; index < args.length; index++) {
        if (args[index] == null) return null;
        result += args[index] + strings[index + 1];
    }
    return result;
};

export const isNullOrUndefined = (
    value: unknown
): value is null | undefined => {
    return value == null;
};

export const compareBasic = (a: number, b: number) => {
    return a === b ? 0 : a > b ? 1 : -1;
};

export const isNullOrEmptyString = (value: string | null): boolean => {
    return isNullOrUndefined(value) || value.trim().length === 0;
};

export const roundToPercent = (num = 0): number => {
    return Math.round(num * 1000) / 10;
};

export const createInternalFilterExpressions = <
    T extends Record<string, unknown>
>(
    filters: Filters<T>,
    skipFilters: string[]
): string => {
    return filters
        .filter(filter => !shouldSkipFilter(filter, skipFilters))
        .map(mapFilter)
        .join(';');
};

const shouldSkipFilter = <T extends Record<string, unknown>>(
    filter: { id: IdType<T>; value: FilterValue },
    skipFilters: string[]
) => {
    return skipFilters.some(hc => hc === filter.id.toString());
};

const mapFilter = <T extends Record<string, unknown>>(filter: {
    id: IdType<T>;
    value: FilterValue;
}) => {
    if (Array.isArray(filter.value)) {
        let flatArray = '';
        const arraySize = filter.value.length;
        filter.value.forEach((value, index) => {
            flatArray = flatArray.concat(
                `${filter.id} eq '${value}'${index < arraySize - 1 ? ';' : ''}`
            );
        });

        return flatArray;
    } else {
        return `${filter.id} startswith '${filter.value}'`;
    }
};

export const joinFilterExpressions = (...filterExpressions: string[]): string =>
    filterExpressions
        .filter(expression => !isNullOrEmptyString(expression))
        .join(';');

// Workaround to handle that the server filter functionality does not support parentheses in the filter query
// When using OR operators in the advanced filter, the base filter must be applied to both sides of the OR statement
export const joinBaseAndAdvancedFilters = (
    baseFilter: string,
    advancedFilter: string
) => {
    if (isNullOrEmptyString(advancedFilter)) {
        return baseFilter;
    }

    var advancedFilterSections = advancedFilter.split('~');
    var filterSections = [];

    for (var i = 0; i < advancedFilterSections.length; i++) {
        filterSections.push(
            joinFilterExpressions(baseFilter, advancedFilterSections[i])
        );
    }

    return filterSections.join('~');
};

export const createFilterExpression = (
    field: string,
    operand: keyof Comparator,
    value: any
) => {
    return !isNullOrEmptyString(value) ? `${field} ${operand} '${value}'` : '';
};
// [['1', '2'], ['1']] => ['1']
// [['1', '2'], ['1'], ['2']] => []
export const intersect = (arr: string[][]): string[] => {
    return arr.reduce((result, current) =>
        result.filter(value => current.includes(value))
    );
};

export function indexBy<
    T extends { [_ in K]: string | number },
    K extends string
>(items: T[], key: K) {
    return items.reduce((result, item) => {
        result[item[key]] = item;
        return result;
    }, {} as Record<T[K], T>);
}

export const isTransmitter = (component = '') => {
    return component.startsWith('@');
};

export function dethunkify<T>(value: T | (() => T)): T {
    return typeof value === 'function' ? (value as () => T)() : value;
}

export const toOption = ({
    id,
    name
}: {
    id: string | null;
    name: string;
}): ReactSelectOption => ({
    label: name,
    value: id ?? ''
});

export const stringToOption = (label: string) => ({
    label,
    value: label
});

export function getFirstDefined(...args: unknown[]) {
    for (let i = 0; i < args.length; i += 1) {
        if (typeof args[i] !== 'undefined') {
            return args[i];
        }
    }
}

/// Encode labels and join labels for URL
export const encodeLabelsToUrl = (
    labels: string[],
    areLabelsAvailable: boolean
) => {
    return areLabelsAvailable
        ? labels.map(label => `&Labels=${encodeText(label)}`).join('')
        : '';
};

/// Encode text using encodeURI, plus replacing # by %23 (UTF-8)
/// The encode of # is not made because browsers use hash char to point to a specific location of the page.
/// In this case, we want to encode it to UTF-8 to be correctly used by the API
export const encodeText = (value: string) =>
    encodeURI(value).replaceAll('#', '%23');

export const getColumnsInfos = (
    instance: TableInstance<any>,
    columns: Column<any>[] | undefined,
    skipColumnsIds?: string[]
) => {
    let columnInfos: ColumnInfo[] = [];

    instance.columns.forEach(c => {
        if (
            !skipColumnsIds ||
            !(skipColumnsIds && skipColumnsIds.find(colId => colId === c.id))
        ) {
            const columnDef =
                columns &&
                columns.find(col => col.accessor === c.id || col.id === c.id);

            columnInfos.push({
                id: c.id,
                title: (columnDef && (columnDef.Header as string)) ?? c.id,
                isVisible: c.isVisible
            } as ColumnInfo);
        }
    });

    return columnInfos;
};

export const getTenaDeviceIdCCC = (deviceId: string): string => {
    if (isTenaDeviceId(deviceId)) {
        const ccc = calculateTenaCCC(deviceId);

        return formatCCC(ccc);
    }

    return deviceId;
};

export const getTenaDeviceIdWithCCC = (deviceId: string): string => {
    if (isTenaDeviceId(deviceId)) {
        const ccc = calculateTenaCCC(deviceId);

        return deviceId.concat(formatCCC(ccc));
    }

    return deviceId;
};

export const isTenaDeviceId = (deviceId: string): boolean => {
    return deviceId.length > 0 && /^\d{12}$/.test(deviceId);
};

export const formatCCC = (ccc: number): string => {
    let numberOfLeadingZeros = 0;
    if (ccc < 10) {
        numberOfLeadingZeros = 2;
    } else if (ccc < 100) {
        numberOfLeadingZeros = 1;
    }

    let formattedCCC = ccc.toString();
    for (let i = 1; i <= numberOfLeadingZeros; i++) {
        formattedCCC = `0${formattedCCC}`;
    }

    return formattedCCC;
};

export const calculateTenaCCC = (deviceId: string): number => {
    return calculateCRC8(deviceId);
};

export const isClutchDevice = (
    transmitter: Transmitter | undefined
): boolean => {
    if (!transmitter) {
        return false;
    }

    const softwareVersion = transmitter?.lastSoftwareVersion;
    if (!softwareVersion) {
        return false;
    }
    if (softwareVersion.startsWith('255.')) {
        return false;
    }

    return !softwareVersion.startsWith('3.');
};

export const isFileNameValid = (fileName: string, version: string) => {
    const versionNumber = version.split('-')[0];
    return fileName.includes(versionNumber);
};

export const isVersionFormatValid = (version: string) => {
    // According to https://semver.org/
    return !!version.match(
        /^(0|[1-9]\d*)((\.(0|[1-9]\d*)){1,2})(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/
    );
};

export const isVersionFormatThreePartValid = (version: string): boolean => {
    // UI validation function for the 'currentVersionsStartsWith' field in AddFirmwareRuleForm
    // Validates that the input matches semantic versioning format: major.minor or major.minor.patch
    // Each section must be 0-999 and no leading zeros (except for 0 itself)
    // Examples: "1.1" (true), "1.1.1" (true), "0.0.0" (true), "999.999.999" (true)
    // Invalid: "1." (false - trailing dot), "1.1.1.1" (false - too many sections), "01.01.03" (false - leading zeros), "1..1" (false - consecutive dots)
    const pattern = /^(0|[1-9]\d{0,2})\.(0|[1-9]\d{0,2})(?:\.(0|[1-9]\d{0,2}))?$/;
    return pattern.test(version);
};

export const getSensorType = (protocol: Protocol, sensorTypeId: number) =>
    {
        var sensorType = '';
        if (protocol === Protocol.BLE && sensorTypeId === 1) {
            sensorType = 'People Counter 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 2) {
            sensorType = 'SCU 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 3) {
            sensorType = 'Sensor Pack Mini 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 4) {
            sensorType = 'Sensor Pack Standard 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 5) {
            sensorType = 'LevelSensor 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 6) {
            sensorType = 'AirBuzz 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 7) {
            sensorType = 'Dose 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 8) {
            sensorType = 'S4 3.0'
        }
        else if (protocol === Protocol.BLE && sensorTypeId === 9) {
            sensorType = 'S26 3.0'
        }
        else if (protocol === Protocol.V1 && sensorTypeId === 2) {
            sensorType = 'SCU'
        }
        else if (protocol === Protocol.V1 && sensorTypeId === 4) {
            sensorType = 'People Counter VRU'
        }
        else if (protocol === Protocol.V1 && sensorTypeId === 5) {
            sensorType = 'S4'
        }
        else if (protocol === Protocol.V1 && sensorTypeId === 6) {
            sensorType = 'Level Sensor'
        }
        else if (protocol === Protocol.V1 && sensorTypeId === 7) {
            sensorType = 'People Counter'
        }
        else if (protocol === Protocol.V2 && sensorTypeId === 2) {
            sensorType = 'SCU 2.0'
        }
        else if (protocol === Protocol.V2 && sensorTypeId === 7) {
            sensorType = 'Sensor Pack Standard'
        }
        else if (protocol === Protocol.V2 && sensorTypeId === 8) {
            sensorType = 'Sensor Pack Mini'
        }

        return sensorType;
    };

export const isBleVersionValid = (version: string): boolean => {
    // Validates BLE version format: only X.Y where X and Y are 0-15, but 0.0 is not allowed
    // Accepts: "0.1", "1.0", "15.15", etc.
    // Rejects: "0.0", "0", "0.1.0", "0.", "16.0", "a.b", etc.
    // Pattern: (0[1-9]|[1-9]|1[0-5])\.(0|[1-9]|1[0-5]) - excludes 0.0 by requiring first digit to be non-zero OR second digit to be non-zero
    const pattern = /^(?!0\.0$)([0-9]|1[0-5])\.([0-9]|1[0-5])$/;
    return pattern.test(version);
};

export const isIdentifiVersionValid = (version: string): boolean => {
    // Validates IdentifiLogger version format: only X.Y where X and Y are 0-255, but 0.0 is not allowed
    // Accepts: "0.1", "1.0", "1.05", "15.15", "255.255", etc.
    // Rejects: "0.0", "0", "0.1.0", "0.", "256.0", "a.b", etc.
    const pattern = /^(?!0\.0$)(?:25[0-5]|2[0-4]\d|1\d\d|0?\d?\d)\.(?:25[0-5]|2[0-4]\d|1\d\d|0?\d?\d)$/;
    return pattern.test(version);
};